import { mentionDocExtension as BaseMentionDocExtension } from '@copera/tiptap/extensions';

import type { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { memo, useCallback, useMemo } from 'react';
import { matchPath } from 'react-router';
import { routes } from 'src/Router/paths';
import { MentionDocNodeView } from '../private/components/MentionDocNodeView';
import { getExtensionOption } from '../private/utils/getExtensionOption';

const DocLink = memo(({ node, editor }: NodeViewProps) => {
  const id = node.attrs.id;
  const options = getExtensionOption(editor, 'mentionDoc');

  const doc = useMemo(() => {
    if (!id) return null;
    return options.getDocumentById(id);
  }, [id, options]);

  const to = useMemo(() => {
    if (!options.getDocumentPath || !doc) return undefined;
    return options.getDocumentPath(doc);
  }, [doc, options]);

  const onClick = useCallback(() => {
    if (!options.onClick) return;
    return () => options.onClick?.(doc!);
  }, [doc, options]);

  if (!doc) return null;

  return (
    <NodeViewWrapper key={id} as="span">
      <MentionDocNodeView doc={doc} to={to} onClick={onClick} />
    </NodeViewWrapper>
  );
});

export const mentionDocExtended = BaseMentionDocExtension.extend({
  addOptions() {
    return {
      registerSubDocument: false,
    };
  },
  addCommandOption(this) {
    return [
      ...(this.options?.registerSubDocument
        ? [
            {
              title: 'Create sub document',
              description: 'Create a new sub document',
              searchTerms: ['sub', 'document'],
              icon: 'tabler:file-plus',
              // @ts-expect-error
              command: async ({ editor, range }) => {
                const options = getExtensionOption(editor, 'mentionDoc');
                if (!options)
                  throw new Error('mentionDoc extension options not found');
                const newDoc = await options.onCreateNewDocument?.();
                editor.commands.mentionDoc({ range, id: newDoc?.id });
              },
            },
          ]
        : []),
      {
        title: 'Link a document',
        description: 'Link an existing document',
        searchTerms: ['link', 'document'],
        icon: 'tabler:file-dots',
        nextMenu: {
          type: 'mentionDoc',
        },
      },
    ];
  },
  addEmbedSuggestionOption() {
    // http://localhost:8080/matheus-ale-116/docs/682b8ff931acaf86134fa456
    // https://dev.copera.ai/matheus-ale-116/docs/682b8ff931acaf86134fa456
    // https://app.copera.ai/matheus-ale-116/docs/682b8ff931acaf86134fa456
    return [
      {
        type: 'mentionDoc',
        name: 'Mention doc',
        regex: [
          /(https?:\/\/)(localhost:8080|dev\.copera\.ai|app\.copera\.ai)\/([^\/]+)\/docs\/([a-zA-Z0-9]+)/g,
        ],
        command: ({ editor, range, link }) => {
          const url = new URL(link);
          const match = matchPath(
            { path: routes.dashboard.docs.view(':id') },
            url.pathname,
          );
          if (!match || !match.params.id) return;
          editor.commands.mentionDoc({ range, id: match.params.id });
        },
      },
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(DocLink, {
      contentDOMElementTag: 'span',
    });
  },
});

export const mentionDocExtension = mentionDocExtended.configure({
  registerSubDocument: true,
});
